import styled, { css } from 'styled-components';

import type { HeaderThemeKeys } from '@/theme/types/components';

import { Box } from '../Box';

export const Wrapper = styled.header<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption: string;
  background: string;
}>`
  top: 0;
  left: 0;
  width: 100%;
  align-items: center;
  position: fixed;
  ${({
    background,
    theme: {
      components: { header },
    },
  }) =>
    background !== 'transparent' &&
    css`
      ${({
        theme: {
          colors: { primary, white },
        },
      }) => `
            background: ${
              background === header.defaultBackground ? primary : white
            };
          `}
    `}

  ${({
    $headerBackground,
    $isShrunk,
    $textColorOption,
    theme: {
      colors,
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    @media (min-width: 768px) {
      ${$textColorOption === 'light' && !$isShrunk
        ? css`
            *,
            li > a,
            li p {
              color: ${colors.black};
              &::after {
                background: ${colors.white};
              }
            }
          `
        : css`
            *,
            li > a,
            li div {
              color: ${headerTheme[$headerBackground].default.text.color};
            }
          `}
    }
  `}

  ${({
    $isShrunk,
    background,
    theme: {
      colors: { primary, white },
    },
  }) =>
    $isShrunk &&
    css`
      background: ${background === 'transparent'
        ? white
        : background === 'yellow'
        ? primary
        : background};
    `}

  ${({ theme: { zIndex } }) => css`
    z-index: ${zIndex.header};
    margin: 0 auto;
  `}
`;

export const HeaderBox = styled(Box)`
  display: flex;
  justify-content: center;
  flex-direction: row;
  position: relative;
`;

export const InnerWrapper = styled.div`
  width: 100%;
  padding: 0px 40px;
  margin: 0 auto;

  ${({
    theme: {
      components: {
        header: { breakpoint, innerWrapper },
      },
    },
  }) => css`
    max-width: ${innerWrapper.maxWidth};

    @media (max-width: ${breakpoint}) {
      grid-template-columns: auto 1fr;
      padding: 0 14px 0;
    }
  `}
  ${({ theme }) =>
    css`
      ${theme.media.sm.down} {
        top: 0;
        right: 0;
        left: 0;
        height: auto;
        z-index: ${theme.zIndex.mobileMenu};
        background-color: ${theme.colors.primary};
      }
    `}
`;

export const DesktopMenu = styled.ul<{ $isAside?: boolean }>`
  display: flex;
  align-items: center;
  justify-self: end;

  list-style: none;

  li {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({
    $isAside,
    theme: {
      components: { header, navigationMenu },
    },
  }) =>
    css`
      gap: ${navigationMenu.gap};

      @media (max-width: ${header.breakpoint}) {
        grid-row: 1;
        grid-column: 3;
        margin-right: ${$isAside ? '0' : navigationMenu.marginRight};
        display: none;
      }
    `}

  svg {
    cursor: pointer;
    transition: color 0.2s;
    color: ${({ $isAside, theme: { colors } }) =>
      $isAside ? colors.white : 'inherit'};

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const MobileMenu = styled(DesktopMenu)`
  display: flex;

  ${({
    $isAside,
    theme: {
      components: { header },
    },
  }) =>
    !$isAside &&
    css`
      display: none;

      @media (max-width: ${header.breakpoint}) {
        display: flex;
      }
    `}
`;

export const LogoBox = styled.div<{
  $headerType: string;
  $logoOnly: boolean;
}>`
  display: block;
  text-align: center;

  a {
    z-index: 99;
  }

  ${({ $headerType, $logoOnly, theme }) =>
    !$logoOnly &&
    $headerType === '140Years' &&
    css`
      @media (max-width: ${theme.breakpoints.sm}) {
        a {
          margin-right: 20px;
        }
      }
      @media (max-width: 375px) {
        a {
          margin-right: 30px;
        }
      }
    `}
`;

export const ItemBox = styled.div<{ $logoOnly?: boolean }>`
  display: block;
  padding: 16px 0;
  text-align: center;
  ${({ $logoOnly }) =>
    $logoOnly
      ? css`
          justify-content: center;
        `
      : css`
          justify-content: space-between;
          position: relative;
        `}
`;

export const MegamenuBox = styled.div<{ $isDisplayed: boolean }>`
  display: flex;
  overflow: hidden;
  max-height: 0;
  padding: 0;

  ${({ $isDisplayed }) =>
    $isDisplayed &&
    css`
      max-height: 46.5px;
      padding: 5px 0 10px 0;
    `}

  ${({ theme }) => css`
    @media (max-width: ${theme.breakpoints.md}) {
      display: none;
    }
  `}
`;

export const HeaderIconBox = styled.div`
  position: absolute;
  top: 20px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  ${({ theme }) => css`
    @media (max-width: ${theme.components.header.breakpoint}) {
      top: calc(50% - 12px);
    }
  `}
`;
